/**
 * ACȚIUNILE RAPIDE — ce poate porni omul de oriunde din aplicație, fără să navigheze.
 *
 * O acțiune rapidă NU e o acțiune din registru și nu inventează niciun drept: e o
 * SCURTĂTURĂ către un ecran care există deja, cu dialogul lui deschis. „Factură nouă”
 * din paletă duce exact în `/facturare` cu dialogul pe care îl deschide butonul
 * „Factură nouă” de acolo — același formular, aceeași acțiune de registru la salvare,
 * aceleași gărzi. Ce se scurtează e drumul până la el, nu poarta.
 *
 * ⛔ RBAC neatins. Fiecare intrare declară `page` — pagina din meniu pe care se cere
 * dreptul — iar filtrarea folosește `pageRights` din `access.ts`, ACELAȘI apel care taie
 * meniul. `write: true` cere dreptul de modificare (creezi ceva), altfel e destul citirea.
 * Nu există aici nicio listă de roluri și niciun drept nou: o intrare fără drept pur și
 * simplu nu apare, iar dacă cineva ar ghici adresa, garda de rută și garda acțiunii îl
 * opresc la fel ca înainte.
 *
 * Fișier fără dependințe de server (ca `nav-tree.ts`): îl citește și filtrul de pe server,
 * și paleta din client.
 */

/** Parametrul din adresă care spune ecranului ce dialog să deschidă la sosire. */
export const PARAM_DESCHIDE = "nou";

export type QuickAction = {
  /** cheie stabilă, folosită și ca valoare a lui `?nou=` pe pagina-țintă */
  key: string;
  /** eticheta din paletă */
  label: string;
  /** unde duce — pagina plus dialogul de deschis */
  href: string;
  /** pagina din meniu pe care se cere dreptul (`pageRights`) — NU un drept nou */
  page: string;
  /** creează ceva → cere dreptul de modificare pe pagina aia; altfel doar citirea */
  write?: boolean;
  /** cuvinte după care se caută în paletă, pe lângă etichetă. Scrie-le FĂRĂ diacritice. */
  keywords: string[];
  /** iconul din paletă — nume din `components/ui/icons.tsx` (componenta `I`) */
  icon?: string;
};

/**
 * Lista. O intrare nouă cere DOUĂ lucruri, altfel n-are ce căuta aici:
 *   1. ecranul-țintă cheamă `useDeschideDinUrl(<key>, …)` — altfel scurtătura duce pe
 *      pagină, dar dialogul nu se deschide și omul rămâne cu un clic în plus, nu în minus;
 *   2. `page` e o pagină REALĂ din `nav-tree.ts` — altfel `pageRights` n-are pe ce cădea
 *      și intrarea ar apărea tuturor.
 */
export const QUICK_ACTIONS: QuickAction[] = [
  // ── Comercial ──
  { key: "client", label: "Client nou", page: "/crm", href: "/crm", write: true,
    keywords: ["client", "partener", "cumparator", "firma", "crm"], icon: "user" },
  { key: "oferta", label: "Ofertă nouă", page: "/vanzari", href: "/vanzari", write: true,
    keywords: ["oferta", "proforma", "cotatie", "vanzari"], icon: "funnel" },
  { key: "comanda", label: "Comandă de vânzare nouă", page: "/vanzari", href: "/vanzari", write: true,
    keywords: ["comanda", "vanzare", "order"], icon: "plus" },
  { key: "lucrare", label: "Lucrare de service nouă", page: "/service", href: "/service", write: true,
    keywords: ["service", "reparatie", "lucrare", "interventie"], icon: "clipboard" },

  // ── Facturare & bani ──
  { key: "factura", label: "Factură nouă", page: "/facturare", href: "/facturare", write: true,
    keywords: ["factura", "facturare", "emite", "invoice"], icon: "receipt" },
  { key: "incasare", label: "Încasare nouă", page: "/facturare", href: "/facturare", write: true,
    keywords: ["incasare", "plata", "chitanta", "bani"], icon: "wallet" },
  { key: "operatiune", label: "Operațiune de trezorerie", page: "/trezorerie", href: "/trezorerie", write: true,
    keywords: ["trezorerie", "banca", "casa", "plata", "operatiune"], icon: "wallet" },

  // ── Aprovizionare & stocuri ──
  /* Un singur dialog de articol (planul Articole §1.17, defectul 36): „Articol nou” deschide dialogul grilei
     `/produse` (catalogul), nu pe cel din Stocuri. `/stocuri?nou=produs` rămâne valabil (același dialog). */
  /* E4 (15.10.2026): produsul nou are fluxul lui în 4 pași (clasă → identitate → caracteristici → site-uri). */
  { key: "produs", label: "Produs nou", page: "/produse/nou", href: "/produse/nou", write: true,
    keywords: ["produs", "articol", "sku", "nomenclator", "catalog", "produs nou"], icon: "box" },
  // ── Catalog (planul Articole §3.1: paleta pe toate cele 9 pagini ale catalogului) ──
  { key: "categorie", label: "Categorie nouă", page: "/produse/clasificare", href: "/produse/clasificare?fila=categorii", write: true,
    keywords: ["categorie", "categorii", "vitrina", "magazin", "structura site", "category"], icon: "layers" },
  { key: "marca", label: "Marcă nouă", page: "/produse/branduri", href: "/produse/branduri", write: true,
    keywords: ["marca", "brand", "producator", "manufacturer"], icon: "tag" },
  { key: "clasa", label: "Clasă de articole nouă", page: "/produse/clasificare", href: "/produse/clasificare", write: true,
    keywords: ["clasa", "clasa noua", "clasificare", "clasa comerciala", "adaos"], icon: "layers" },
  { key: "miscare", label: "Mișcare de stoc", page: "/stocuri", href: "/stocuri", write: true,
    keywords: ["miscare", "stoc", "intrare", "iesire", "transfer"], icon: "layers" },
  { key: "achizitie", label: "Comandă de achiziție nouă", page: "/achizitii", href: "/achizitii", write: true,
    keywords: ["achizitie", "aprovizionare", "comanda", "furnizor", "po"], icon: "plus" },
  { key: "furnizor", label: "Furnizor nou", page: "/achizitii/furnizori", href: "/achizitii/furnizori", write: true,
    keywords: ["furnizor", "supplier", "partener"], icon: "user" },
  { key: "receptie", label: "Recepție marfă (NIR)", page: "/receptii-marfa", href: "/receptii-marfa", write: true,
    keywords: ["receptie", "nir", "intrare", "marfa"], icon: "truck" },
  { key: "depozit", label: "Depozit nou", page: "/depozite", href: "/depozite", write: true,
    keywords: ["depozit", "gestiune", "warehouse"], icon: "warehouse" },

  // ── Producție ──
  { key: "retetar", label: "Rețetar nou", page: "/productie", href: "/productie", write: true,
    keywords: ["retetar", "bom", "reteta", "productie"], icon: "layers" },

  // ── Birou & personal ──
  { key: "angajat", label: "Angajat nou", page: "/hr", href: "/hr", write: true,
    keywords: ["angajat", "salariat", "personal", "hr"], icon: "user" },
  { key: "campanie", label: "Campanie nouă", page: "/marketing/online", href: "/marketing/online", write: true,
    keywords: ["campanie", "marketing", "promovare"], icon: "mail" },
];

/** Text fără diacritice, fără majuscule, fără spații la capete — potrivirea din paletă nu
 *  se împiedică de „ă” și nici de „Ă”. */
export function normalizeaza(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/**
 * Potrivirea unui termen pe o intrare de paletă, cu SCOR — ca „fact” să scoată „Factură nouă”
 * înaintea lui „Recepție marfă”, nu în ordinea în care s-a scris lista.
 *
 * 0 = nu se potrivește. Mai mare = mai bun.
 */
export function scorPotrivire(termen: string, eticheta: string, cuvinte: string[] = []): number {
  const t = normalizeaza(termen);
  if (!t) return 1;
  const e = normalizeaza(eticheta);
  if (e === t) return 100;
  if (e.startsWith(t)) return 80;
  /* început de CUVÂNT din etichetă: „noua” prinde „Factură nouă”, dar „ura” nu prinde nimic —
     potrivirea la mijloc de cuvânt scoate zgomot într-o listă scurtă. */
  if (e.split(/\s+/).some((w) => w.startsWith(t))) return 60;
  for (const k of cuvinte) {
    const kk = normalizeaza(k);
    if (kk === t) return 55;
    if (kk.startsWith(t)) return 40;
  }
  if (e.includes(t)) return 20;
  return 0;
}
